import { useMemo } from "react";
import { Badge, Box, Button, Flex, Group, Text } from "@mantine/core";
import { IconEye, IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import TableRowLinkOverlay from "@/components/shared/TableRowLinkOverlay";
import TableRowLinkTableStyles from "@/components/shared/TableRowLinkTableStyles";
import { getCollaborationRoleLabel, getIndustryLabel } from "@/components/shared/constants/general.const";
import InterestRequestActions from "@/pages/interests/components/InterestRequestActions";
import { buildCompanyDiscoverPath, companyProfileAriaLabel } from "@/shared/utils/companyProfileLink.util";
import {
  buildDashboardReturnToForInterestRow,
  type RequestFilter,
} from "@/pages/dashboard/dashboardTab.const";
import { workspaceBackLinkState } from "@/shared/utils/workspaceBackNavigation.util";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";

type CompanyInfo = {
  id: string;
  gtid: string;
  legalName?: string;
  headquartersCountry?: string;
  industryCategory?: string[];
  status?: string;
};

export type InterestRequestListSource = "received" | "sent" | "suggested";

const LIST_SOURCE_LABEL: Record<InterestRequestListSource, string> = {
  received: "Received",
  sent: "Sent",
  suggested: "Suggested",
};

const LIST_SOURCE_BADGE_COLOR: Record<InterestRequestListSource, string> = {
  received: "teal",
  sent: "orange",
  suggested: "primary",
};

export type InterestRequestTableRow = {
  rowKind: "request" | "suggested";
  listSource: InterestRequestListSource;
  matchId: string;
  company: CompanyInfo;
  collaborationStatus: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
  requesterSignedNDA: boolean;
  receiverSignedNDA: boolean;
  paymentDeadline?: Date | string | null;
  requesterRole: string | null;
  expectedPartnerRole: string | null;
};

/** Matches `MatchedPrfofielTab` express-interest role labels. */
function collaborationRoleText(role: string | null | undefined) {
  if (!role?.trim()) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Text size="sm" c="slate.7" fw={500}>
      {getCollaborationRoleLabel(role)}
    </Text>
  );
}

export function toInterestRequestTableRows(
  requests: any[],
  isRequester: boolean
): InterestRequestTableRow[] {
  return (requests ?? [])
    .map((request) => {
      const requesterCompany = request.requester as CompanyInfo | undefined;
      const receiverCompany = request.receiver as CompanyInfo | undefined;
      const company: CompanyInfo | undefined = isRequester ? receiverCompany : requesterCompany;
      if (!company?.id) return null;
      return {
        rowKind: "request" as const,
        listSource: isRequester ? "sent" : "received",
        matchId: request.id,
        company,
        collaborationStatus: request.status,
        isRequester,
        requesterPaid: request.requesterPaid,
        receiverPaid: request.receiverPaid,
        requesterSignedNDA: request.requesterSignedNDA,
        receiverSignedNDA: request.receiverSignedNDA,
        paymentDeadline: request.paymentDeadline,
        requesterRole: request.requesterRole ?? null,
        expectedPartnerRole: request.expectedPartnerRole ?? null,
      };
    })
    .filter(Boolean) as InterestRequestTableRow[];
}

interface InterestRequestsTableProps {
  /** Current dashboard requests filter — drives workspace back link per row. */
  requestFilter?: RequestFilter;
  /** Pre-built rows (e.g. suggested partners). When set, `requests` / `isRequester` are ignored. */
  rows?: InterestRequestTableRow[];
  requests?: any[];
  isRequester?: boolean;
  isLoading: boolean;
  actionLoading: string | null;
  onAccept: (matchId: string) => void;
  onDecline: (matchId: string) => void;
  onMakePayment: (matchId: string) => void;
  onViewProfile: (companyId: string) => void;
  onOpenWorkspace: (matchId: string) => void;
}

export default function InterestRequestsTable({
  requestFilter = "all",
  rows: rowsProp,
  requests,
  isRequester = false,
  isLoading,
  actionLoading,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
}: InterestRequestsTableProps) {
  const data = useMemo(() => {
    if (rowsProp) return rowsProp;
    return toInterestRequestTableRows(requests ?? [], isRequester);
  }, [rowsProp, requests, isRequester]);

  const showRoleColumns = data.length === 0 || data.some((r) => r.rowKind === "request");

  const columns = useMemo<MRT_ColumnDef<InterestRequestTableRow>[]>(
    () => {
      const roleColumns: MRT_ColumnDef<InterestRequestTableRow>[] = showRoleColumns
        ? [
            {
              id: "requesterCollaborationRole",
              header: "Your role in this collaboration",
              size: 200,
              Cell: ({ row }) =>
                row.original.rowKind === "suggested"
                  ? collaborationRoleText(null)
                  : collaborationRoleText(row.original.requesterRole),
            },
            {
              id: "expectedPartnerRole",
              header: "Expected Partner Role",
              size: 200,
              Cell: ({ row }) =>
                row.original.rowKind === "suggested"
                  ? collaborationRoleText(null)
                  : collaborationRoleText(row.original.expectedPartnerRole),
            },
          ]
        : [];

      return [
      {
        id: "company",
        header: "Company",
        size: 200,
        Cell: ({ row }) => {
          const { company, rowKind } = row.original;
          const profilePath = buildCompanyDiscoverPath(company.id);
          const linkState = workspaceBackLinkState(
            buildDashboardReturnToForInterestRow(requestFilter, rowKind, row.original.listSource),
          );
          return (
            <>
              <TableRowLinkOverlay
                to={profilePath}
                ariaLabel={companyProfileAriaLabel(company.gtid)}
                linkState={linkState}
              />
              <Group gap={6} wrap="nowrap">
                <Text size="sm" fw={500} c="primary.7" style={{ cursor: "pointer" }}>
                  {company.gtid || "N/A"}
                </Text>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled
                    size={16}
                    style={{ color: "var(--mantine-color-primary-6)", flexShrink: 0 }}
                  />
                )}
              </Group>
            </>
          );
        },
      },
      {
        id: "industry",
        header: "Industry",
        size: 180,
        Cell: ({ row }) => {
          const cats = row.original.company.industryCategory;
          if (!Array.isArray(cats) || cats.length === 0) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          return (
            <Text size="sm">
              {getIndustryLabel(cats[0])}
              {cats.length > 1 ? ` +${cats.length - 1}` : ""}
            </Text>
          );
        },
      },
      ...roleColumns,
      {
        id: "location",
        header: "Location",
        size: 120,
        Cell: ({ row }) => {
          const loc = row.original.company.headquartersCountry;
          if (!loc?.trim()) {
            return (
              <Text size="sm" c="dimmed">
                —
              </Text>
            );
          }
          const flag = flagEmojiFromHeadquartersCountry(loc);
          return (
            <Flex align="center" gap={6} wrap="nowrap">
              {flag ? (
                <Text
                  component="span"
                  fz={18}
                  lh={1}
                  role="img"
                  aria-label={`${loc} flag`}
                  style={{ flexShrink: 0 }}
                >
                  {flag}
                </Text>
              ) : null}
              <Text size="sm" style={{ minWidth: 0 }}>
                {loc}
              </Text>
            </Flex>
          );
        },
      },
      {
        id: "listSource",
        header: "Status",
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => (
          <Badge
            variant="light"
            color={LIST_SOURCE_BADGE_COLOR[row.original.listSource]}
            radius="sm"
            size="sm"
            styles={{ root: { textTransform: "none", fontWeight: 600 } }}
          >
            {LIST_SOURCE_LABEL[row.original.listSource]}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 200,
        enableSorting: false,
        Cell: ({ row }) => {
          const r = row.original;
          if (r.rowKind === "suggested") {
            return (
              <Box data-surface-action>
                <Button
                  variant="outline"
                  size="sm"
                  radius="lg"
                  leftSection={<IconEye size={16} />}
                  onClick={() => onViewProfile(r.company.id)}
                >
                  View profile
                </Button>
              </Box>
            );
          }
          return (
            <Box data-surface-action>
              <InterestRequestActions
                matchId={r.matchId}
                companyId={r.company.id}
                collaborationStatus={r.collaborationStatus}
                isRequester={r.isRequester}
                requesterPaid={r.requesterPaid}
                receiverPaid={r.receiverPaid}
                requesterSignedNDA={r.requesterSignedNDA}
                receiverSignedNDA={r.receiverSignedNDA}
                paymentDeadline={r.paymentDeadline}
                onAccept={onAccept}
                onDecline={onDecline}
                onMakePayment={onMakePayment}
                onViewProfile={onViewProfile}
                onOpenWorkspace={onOpenWorkspace}
                isLoading={actionLoading === r.matchId}
                variant="table"
              />
            </Box>
          );
        },
      },
    ];
    },
    [
      requestFilter,
      actionLoading,
      showRoleColumns,
      onAccept,
      onDecline,
      onMakePayment,
      onOpenWorkspace,
      onViewProfile,
    ],
  );

  return (
    <Box className="gt-table-row-link">
      <TableRowLinkTableStyles />
      <MantineReactTable
        columns={columns}
        data={data}
        layoutMode="semantic"
        state={{ isLoading }}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableSorting={false}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        mantineTableContainerProps={{ style: { width: "100%", overflowX: "auto" } }}
        mantineTableBodyRowProps={() => ({
          "data-row-link": "",
          style: { cursor: "pointer", position: "relative" },
        })}
        mantineTableBodyCellProps={({ column }) => ({
          ...(column.id === "company"
            ? { "data-row-link-host": "", style: { position: "relative", overflow: "visible" } }
            : {}),
          ...(column.id === "actions" ? { "data-action-cell": "" } : {}),
        })}
      />
    </Box>
  );
}
